import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { TIMEZONE } from './constants';

export type IsoDate = string; // yyyy-MM-dd
export type HHMM = string; // HH:mm

export const ALBANIAN_DAYS = [
  'E diel',
  'E hënë',
  'E martë',
  'E mërkurë',
  'E enjte',
  'E premte',
  'E shtunë',
] as const;
export const ALBANIAN_MONTHS = [
  'janar',
  'shkurt',
  'mars',
  'prill',
  'maj',
  'qershor',
  'korrik',
  'gusht',
  'shtator',
  'tetor',
  'nëntor',
  'dhjetor',
] as const;

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(v: unknown): v is IsoDate {
  if (typeof v !== 'string' || !ISO_DATE_RE.test(v)) return false;
  const d = parseDateOnly(v);
  return dateISO(d) === v;
}

/** Prototype `isoToday()` — today's calendar date in Europe/Belgrade. */
export function isoToday(now: Date = new Date()): IsoDate {
  return formatInTimeZone(now, TIMEZONE, 'yyyy-MM-dd');
}

/** Current wall-clock time HH:mm in Europe/Belgrade. */
export function nowHHMM(now: Date = new Date()): HHMM {
  return formatInTimeZone(now, TIMEZONE, 'HH:mm');
}

/** Prototype `parseDateOnly(date)` — yyyy-MM-dd → Date at 00:00 UTC (day arithmetic only). */
export function parseDateOnly(date: string | null | undefined): Date {
  const p = String(date ?? '')
    .split('-')
    .map(Number);
  return new Date(Date.UTC(p[0] || 1970, (p[1] || 1) - 1, p[2] || 1));
}

/** Prototype `dateISO(d)`. */
export function dateISO(d: Date): IsoDate {
  return d.toISOString().slice(0, 10);
}

/** Prototype `addDays(d, n)` — pure calendar arithmetic, no DST drift. */
export function addDays(d: IsoDate, n: number): IsoDate {
  const x = parseDateOnly(d);
  x.setUTCDate(x.getUTCDate() + n);
  return dateISO(x);
}

/** Prototype `mondayOf(date)` — Monday (UTC midnight) of the ISO week containing `date`. */
export function mondayOf(date: IsoDate): Date {
  const d = parseDateOnly(date);
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - day + 1);
  return d;
}

/** 0 = Sunday … 6 = Saturday for a yyyy-MM-dd string. */
export function dayOfWeek(date: IsoDate): number {
  return parseDateOnly(date).getUTCDay();
}

/** Prototype `daysInRange(from, to)` — inclusive list of ISO dates (empty when to < from). */
export function daysInRange(from: IsoDate, to: IsoDate): IsoDate[] {
  const out: IsoDate[] = [];
  if (!from || !to || to < from) return out;
  let cur = from;
  while (cur <= to && out.length < 5000) {
    out.push(cur);
    cur = addDays(cur, 1);
  }
  return out;
}

/** Difference in whole days (b − a). */
export function diffDays(a: IsoDate, b: IsoDate): number {
  return Math.round((parseDateOnly(b).getTime() - parseDateOnly(a).getTime()) / 86400000);
}

/**
 * Display format dd/MM/yyyy, the European order with slashes the shop asked for (the prototype used dots,
 * task.md §1); `—` for empty.
 */
export function fmtDate(d: IsoDate | null | undefined): string {
  if (!d || !ISO_DATE_RE.test(d)) return '—';
  const [y, m, day] = d.split('-');
  return `${day}/${m}/${y}`;
}

/** What a date field shows while it is empty. */
export const DATE_INPUT_PLACEHOLDER = 'DD/MM/YYYY';

/**
 * NEW — a date as somebody types it: DD/MM/YYYY, with dots or dashes too, without separators (29092026),
 * or already ISO (2026-09-29, what a paste from elsewhere or a test brings). Null until it is a real
 * calendar date with a four-digit year, so a half-typed "29/09/20" is never taken for the year 20.
 */
export function parseDateInput(text: string): IsoDate | null {
  const t = text.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  const dmy = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(t) ?? /^(\d{2})(\d{2})(\d{4})$/.exec(t);
  const y = iso ? iso[1] : dmy?.[3];
  const m = iso ? iso[2] : dmy?.[2];
  const d = iso ? iso[3] : dmy?.[1];
  if (!y || !m || !d) return null;
  const value = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  const date = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value ? value : null;
}

/** Instant → `dd/MM/yyyy HH:mm` in Europe/Belgrade (prototype `dateTime`, with slashes like fmtDate). */
export function dateTime(iso: string | Date | null | undefined): string {
  if (!iso) return '—';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return formatInTimeZone(d, TIMEZONE, 'dd/MM/yyyy HH:mm');
}

/** Instant → `HH:mm` in Europe/Belgrade. */
export function timeOf(iso: string | Date | null | undefined): HHMM {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatInTimeZone(d, TIMEZONE, 'HH:mm');
}

/** Instant → yyyy-MM-dd in Europe/Belgrade. */
export function dateOf(iso: string | Date): IsoDate {
  const d = iso instanceof Date ? iso : new Date(iso);
  return formatInTimeZone(d, TIMEZONE, 'yyyy-MM-dd');
}

/** yyyy-MM-dd + HH:mm in Europe/Belgrade → UTC instant. */
export function belgradeToUtc(date: IsoDate, time: HHMM): Date {
  return fromZonedTime(`${date}T${time.length === 5 ? time : '00:00'}:00`, TIMEZONE);
}

/** Prototype top-bar label: `E premte, 05 shtator 2026`. */
export function longDateLabel(date: IsoDate): string {
  const d = parseDateOnly(date);
  return `${ALBANIAN_DAYS[d.getUTCDay()]}, ${String(d.getUTCDate()).padStart(2, '0')} ${ALBANIAN_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** Prototype `minsBetween(a, b)` for HH:mm strings — never negative, 0 when missing. */
export function minsBetween(a: HHMM | null | undefined, b: HHMM | null | undefined): number {
  if (!a || !b) return 0;
  const A = a.split(':').map(Number);
  const B = b.split(':').map(Number);
  return Math.max(0, (B[0] ?? 0) * 60 + (B[1] ?? 0) - ((A[0] ?? 0) * 60 + (A[1] ?? 0)));
}

/** Prototype `fmtMinutes(m)` → `7h 05m`. */
export function fmtMinutes(m: number | null | undefined): string {
  const v = Math.max(0, Math.round(Number(m) || 0));
  return `${Math.floor(v / 60)}h ${String(v % 60).padStart(2, '0')}m`;
}

/** `yyyy-MM` key for a date. */
export function monthKey(date: IsoDate): string {
  return date.slice(0, 7);
}

/** First day of the month containing `date`. */
export function firstOfMonth(date: IsoDate): IsoDate {
  return `${date.slice(0, 7)}-01`;
}

/** Number of days in the month of `date`. */
export function daysInMonth(date: IsoDate): number {
  const d = parseDateOnly(date);
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
}
